import { Failure, Ok } from "@megaron/result";
import {
  FieldSerializers,
  Serializer,
  serializerExtensions,
  SerializerExtensions,
  Serializers,
} from "@megaron/serializers";

type ExtractParamNames<S extends string> = S extends `${string}{${infer Param}}${infer Rest}`
  ? Param | ExtractParamNames<Rest>
  : never;

type ReplaceParams<
  S extends string,
  Params extends { [key: string]: string },
> = S extends `${infer Start}{${infer Param}}${infer Rest}`
  ? `${Start}${Params[Param]}${ReplaceParams<Rest, Params>}`
  : S;

type UriParams<TTemplate extends string> = {
  [key in ExtractParamNames<TTemplate>]: any;
};

export type UriSpec<TTemplate extends string, TParams extends UriParams<TTemplate> = never> = {
  fields: Serializers.FieldSerializers<TParams>;
};

type FieldsArg<TParams extends { [key: string]: any }> =
  {} extends Serializers.FieldSerializers<TParams>
    ? [fields?: Serializers.FieldSerializers<TParams>]
    : [fields: Serializers.FieldSerializers<TParams>];

export const UriSpec = <TTemplate extends string, TParams extends UriParams<TTemplate> = never>(
  ...args: FieldsArg<TParams>
): UriSpec<TTemplate, TParams> => ({
  fields: (args[0] ?? {}) as Serializers.FieldSerializers<TParams>,
});

type UriQueryString = `?${string}` | "";

type Uri<
  TTemplate extends string,
  TParams extends UriParams<TTemplate>,
> = `${ReplaceParams<TTemplate, TParams>}${UriQueryString}`;

const getParamNames = (template: string) => {
  const pathParamNames: string[] = [];
  for (const match of template.matchAll(/\{(\w+)\}/g)) {
    pathParamNames.push(match[1]);
  }
  return pathParamNames;
};

const constructUri =
  <TTemplate extends string, TParams extends UriParams<TTemplate>>(
    template: TTemplate,
    paramsSerializers: FieldSerializers<TParams>,
  ) =>
  (params: TParams) => {
    const pathParamSet = new Set(getParamNames(template));
    const serializedParams = Serializers.object(paramsSerializers).partial().serialize(params) as Record<
      string,
      unknown
    >;

    let uri = template as string;
    const queryParams: Record<string, string> = {};

    for (const [key, value] of Object.entries(serializedParams)) {
      if (value === undefined) continue;
      if (pathParamSet.has(key)) {
        uri = uri.replace(`{${key}}`, encodeURIComponent(String(value)));
      } else {
        queryParams[key] = String(value);
      }
    }

    const queryString = new URLSearchParams(queryParams).toString();
    if (queryString) uri = `${uri}?${queryString}`;

    return uri as Uri<TTemplate, TParams>;
  };

const buildTemplateRegex = (template: string) => {
  const parts = template.split(/\{(\w+)\}/g);
  let regexStr = "^";
  for (let i = 0; i < parts.length; i++) {
    if (i % 2 === 0) {
      regexStr += parts[i].replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    } else {
      regexStr += "(.+)";
    }
  }
  regexStr += "(?:\\?(.*))?$";
  return new RegExp(regexStr);
};

const extractParams =
  <TTemplate extends string, TParams extends UriParams<TTemplate>>(
    template: TTemplate,
    paramsSerializers: FieldSerializers<TParams>,
  ) =>
  (uri: Uri<TTemplate, TParams>): TParams => {
    const pathParamNames = getParamNames(template);
    const regex = buildTemplateRegex(template);
    const match = regex.exec(uri);

    if (!match) throw new Error("UriMismatch");

    const rawParams: Record<string, unknown> = {};

    for (let i = 0; i < pathParamNames.length; i++) {
      rawParams[pathParamNames[i]] = decodeURIComponent(match[i + 1]);
    }

    const queryString = match[pathParamNames.length + 1];
    if (queryString) {
      for (const [key, value] of new URLSearchParams(queryString).entries()) {
        rawParams[key] = value;
      }
    }

    return Serializers.object(paramsSerializers).deserialize(rawParams).assertOk().value;
  };

type BaseUriSchema<T> = { [K in keyof T & string]: UriSpec<K, UriParams<K>> };

export type UriOfSchema<T extends BaseUriSchema<T>> = {
  [K in keyof T & string]: T[K] extends UriSpec<infer K, infer KParams> ? Uri<K, KParams> : never;
}[keyof T & string];

export const UriSchema = <T extends BaseUriSchema<T>>(schema: T) => schema;

export const uriSerializer = <TSchema extends BaseUriSchema<TSchema>>(
  schema: TSchema,
): Serializer<UriOfSchema<TSchema>> & SerializerExtensions<UriOfSchema<TSchema>> => ({
  serialize: (value) => value,
  deserialize: (value) => {
    const strResult = Serializers.string.deserialize(value);
    if (strResult.isFailure) return strResult;
    const str = strResult.value;

    for (const template in schema) {
      const fieldSerializers = schema[template].fields;
      try {
        const params = extractParams(template, fieldSerializers)(str as any);
        return Ok(constructUri(template, fieldSerializers)(params ?? {}) as UriOfSchema<TSchema>);
      } catch (e) {
        /* empty */
      }
    }
    return Failure("UnknownUri");
  },

  ...serializerExtensions(),
});

type OptionalIfUndefined<T> = {
  [K in keyof T as undefined extends T[K] ? K : never]?: T[K];
} & {
  [K in keyof T as undefined extends T[K] ? never : K]: T[K];
};

type SchemaParams<TSchema, TTemplate extends keyof TSchema & string> =
  TSchema[TTemplate] extends UriSpec<TTemplate, infer TParams> ? OptionalIfUndefined<TParams> : never;

type BuilderArgs<TSchema extends BaseUriSchema<TSchema>, TTemplate extends keyof TSchema & string> =
  TTemplate extends TTemplate
    ? {} extends SchemaParams<TSchema, TTemplate>
      ? [TTemplate] | [TTemplate, SchemaParams<TSchema, TTemplate>]
      : [TTemplate, SchemaParams<TSchema, TTemplate>]
    : never;

type BuiltUri<TSchema extends BaseUriSchema<TSchema>, TTemplate extends keyof TSchema & string> =
  SchemaParams<TSchema, TTemplate> extends UriParams<TTemplate>
    ? Uri<TTemplate, SchemaParams<TSchema, TTemplate>>
    : never;

export type UriBuilder<TSchema extends BaseUriSchema<TSchema>> = {
  <TTemplate extends keyof TSchema & string>(...args: BuilderArgs<TSchema, TTemplate>): BuiltUri<TSchema, TTemplate>;

  serializer: Serializer<UriOfSchema<TSchema>> & SerializerExtensions<UriOfSchema<TSchema>>;
  isUri: (value: unknown) => value is UriOfSchema<TSchema>;
};

export type UriBuilderMatcher<TSchema extends BaseUriSchema<TSchema>, TReturn> = {
  <TTemplate extends keyof TSchema & string>(...args: BuilderArgs<TSchema, TTemplate>): TReturn;
};

export const uriMatcher =
  <TSchema extends BaseUriSchema<TSchema>>(schema: TSchema) =>
  <TReturn>(handlers: {
    [K in keyof TSchema & string]: TSchema[K] extends UriSpec<K, infer KParams> ? (params: KParams) => TReturn : never;
  }) => {
    const matcher = (uri: UriOfSchema<TSchema>): TReturn => {
      for (const template in schema) {
        const fieldSerializers = schema[template].fields;
        try {
          const params = extractParams(template, fieldSerializers)(uri as any);
          return (handlers[template] as (params: unknown) => TReturn)(params);
        } catch (e) {
          /* empty */
        }
      }
      throw new Error("NoMatch");
    };

    matcher.try = (uri: UriOfSchema<TSchema> | unknown): TReturn | undefined => {
      try {
        return matcher(uri as UriOfSchema<TSchema>);
      } catch (e) {
        return undefined;
      }
    };

    matcher.uri = ((...[template, params]: [keyof TSchema & string, unknown?]): TReturn => {
      const uri = constructUri(template, schema[template].fields)(params ?? {}) as UriOfSchema<TSchema>;
      return matcher(uri);
    }) as UriBuilderMatcher<TSchema, TReturn>;

    return matcher;
  };

export const UriBuilder = <TSchema extends BaseUriSchema<TSchema>>(schema: TSchema): UriBuilder<TSchema> => {
  const builder = (...[template, params]: [keyof TSchema & string, unknown?]) =>
    constructUri(template, schema[template].fields)(params ?? {});

  builder.serializer = uriSerializer(schema);

  builder.isUri = (value: unknown): value is UriOfSchema<TSchema> => {
    const result = builder.serializer.deserialize(value);
    return result.isOk;
  };

  return builder as UriBuilder<TSchema>;
};
